import type { Item } from '@mystiq/contracts';
import { useQuery } from '@tanstack/react-query';
import { keys, useWorkspaceMutation } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { timeAgo, timeUntil } from '../lib/format.ts';
import { CopyField, ErrorLine, Modal } from './ui.tsx';

/**
 * One read-only link per item. Readers see exactly the shared version of this
 * item and nothing else; later edits (or new Site versions) appear only after
 * "Share the latest version". A Site can share the version being viewed.
 */
export function ShareDialog({
  ws,
  item,
  revision,
  onClose,
}: {
  ws: string;
  item: Item;
  /** The version on screen (Sites), which the link can show instead of the current one. */
  revision?: { revisionId: string; label: string } | undefined;
  onClose(): void;
}) {
  const api = useApi();
  const shares = useQuery({ queryKey: keys.shares(ws, item.id), queryFn: () => api.shares(ws, item.id) });
  const earlier = revision && revision.revisionId !== item.currentRevisionId ? revision : undefined;
  const create = useWorkspaceMutation(ws, () => api.createShare(ws, item.id, earlier ? { revisionId: earlier.revisionId } : {}));
  const update = useWorkspaceMutation(ws, ({ shareId, revisionId }: { shareId: string; revisionId?: string }) => api.updateShare(ws, shareId, revisionId));
  const revoke = useWorkspaceMutation(ws, (shareId: string) => api.revokeShare(ws, shareId));
  const share = shares.data?.[0];
  return (
    <Modal title="Share a read-only link" onClose={onClose}>
      <p className="meta">
        Anyone with the link can {item.kind === 'file' ? 'read and download this file' : item.kind === 'site' ? 'use this Site' : 'read this item'}. They see only this
        version of it: not your workspace, folders, sources or other outputs.{earlier && shares.isSuccess && !shares.data.length ? ` The link will show ${earlier.label}.` : ''}
      </p>
      {shares.isLoading && <p className="meta">Loading…</p>}
      {shares.isSuccess && !share && (
        <div>
          <button className="button button-primary" disabled={create.isPending} onClick={() => create.mutate(undefined)}>
            Create link
          </button>
        </div>
      )}
      {share && (
        <div className="stack">
          <CopyField label="Read-only link" value={share.url} />
          <p className="meta">
            {share.latest ? 'Shows the current version.' : (
              <>
                <span className="state">Shows an earlier version.</span> This item changed after it was shared.
              </>
            )}{' '}
            {share.viewCount === 1 ? 'Opened once' : `Opened ${share.viewCount} times`}
            {share.lastViewedAt ? `, last ${timeAgo(share.lastViewedAt)}` : ''}.
            {share.expiresAt ? ` Stops working ${timeUntil(share.expiresAt)}.` : ''}
          </p>
          <div className="toolbar">
            {!share.latest && (
              <button className="button" disabled={update.isPending} onClick={() => update.mutate({ shareId: share.id })}>
                Share the latest version
              </button>
            )}
            {earlier && share.revisionId !== earlier.revisionId && (
              <button className="button" disabled={update.isPending} onClick={() => update.mutate({ shareId: share.id, revisionId: earlier.revisionId })}>
                Share {earlier.label} instead
              </button>
            )}
            <button className="button" disabled={revoke.isPending} onClick={() => revoke.mutate(share.id)}>
              Stop sharing
            </button>
          </div>
          <p className="meta">Stopping the link takes effect at once. Copies people already downloaded cannot be recalled.</p>
        </div>
      )}
      <ErrorLine error={shares.error ?? create.error ?? update.error ?? revoke.error} />
      <div className="modal-actions">
        <button className="button" onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}
